import React from "react";
import classnames from "classnames";
import SbEditable from "storyblok-react";
import {
  render,
  MARK_LINK,
  NODE_PARAGRAPH,
  NODE_OL,
  NODE_UL,
} from "storyblok-rich-text-react-renderer";
import Link from "@/components/Link";
import styles from "./value-prop-cards.module.scss";
import type {
  ValuePropCardItemStoryblok,
  ValuePropCardsStoryblok,
} from "./types";

interface Props {
  blok: ValuePropCardsStoryblok;
}

type Description = ValuePropCardItemStoryblok["description"];

// Storyblok keeps previously saved plain-text values after the field is changed
// to richtext, so both shapes reach this component until every card is re-saved.
// An empty richtext doc is a single childless paragraph; rendering it would add
// a blank <p> and its 16px card gap, so treat it as no description. Any other
// node type (a list, a rule) counts as content.
const hasContent = (description: Exclude<Description, string | undefined>) =>
  !!description?.content?.some(
    (node) => node.type !== "paragraph" || node.content?.length
  );

const renderDescription = (description: Description) => {
  if (!description) return null;

  if (typeof description === "string") {
    return <p className={styles.valuePropCardDesc}>{description}</p>;
  }

  if (!hasContent(description)) return null;

  const listClasses = classnames("ef-list", styles.valuePropCardDesc);

  return render(description, {
    markResolvers: {
      // email links arrive as a bare address; overriding the mark means adding
      // the scheme here too. (TextBlock's linkRenderer has the same gap.)
      [MARK_LINK]: (children, props) => (
        <Link
          className="ef-link"
          target={props.target}
          href={
            props.linktype === "email" && props.href
              ? `mailto:${props.href}`
              : props.href || ""
          }
        >
          {children}
        </Link>
      ),
    },
    nodeResolvers: {
      [NODE_PARAGRAPH]: (children) => (
        <p className={styles.valuePropCardDesc}>{children}</p>
      ),
      [NODE_OL]: (children) => <ol className={listClasses}>{children}</ol>,
      [NODE_UL]: (children) => <ul className={listClasses}>{children}</ul>,
    },
  });
};

const ValuePropCards = ({ blok }: Props) => {
  const cards = Array.isArray(blok.cards) ? blok.cards : [];
  const variant = blok.variant || "bordered";
  const columns = blok.columns || "3";

  return (
    // @ts-expect-error
    <SbEditable content={blok}>
      <div className={styles.valuePropCards}>
        <div
          className={classnames(styles.valuePropGrid, {
            [styles.valuePropGridFour]: columns === "4",
          })}
        >
          {cards.length
            ? cards.map((card) => (
                <div
                  key={card._uid}
                  className={classnames(styles.valuePropCard, {
                    [styles.valuePropCardGradient]: variant === "gradient",
                    [styles.valuePropCardPlain]: variant === "plain",
                  })}
                >
                  {card.eyebrow ? (
                    <p className={styles.valuePropEyebrow}>{card.eyebrow}</p>
                  ) : null}
                  {card.icon?.filename ? (
                    // eslint-disable-next-line @next/next/no-img-element -- small decorative icons; shared cover wrapper doesn't support contain behavior
                    <img
                      src={card.icon.filename}
                      alt=""
                      className={styles.valuePropIcon}
                      loading="lazy"
                    />
                  ) : null}
                  {card.title ? (
                    <h3 className={styles.valuePropCardTitle}>{card.title}</h3>
                  ) : null}
                  {card.meta ? (
                    <p className={styles.valuePropMeta}>{card.meta}</p>
                  ) : null}
                  {renderDescription(card.description)}
                </div>
              ))
            : null}
        </div>
      </div>
    </SbEditable>
  );
};

export default ValuePropCards;
